import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Calendar, ArrowRight, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { safeFetch } from '@/lib/safeFetch';

interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  category: string;
  image: string;
  readTime: string;
}

const Blog = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      setIsLoading(true);
      const data = await safeFetch<any[]>(
        supabase
          .from('blog_posts')
          .select('*')
          .eq('status', 'published')
          .order('created_at', { ascending: false }),
        [],
        'Loading research publications'
      );

      const formattedPosts = data.map(post => ({
        slug: post.slug,
        title: post.title,
        excerpt: post.short_description,
        date: post.created_at,
        category: post.tags?.split(',')[0] || 'Uncategorized',
        image: post.thumbnail || 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&auto=format&fit=crop&q=60',
        readTime: `${Math.max(1, Math.ceil((post.content?.split(' ').length || 0) / 200))} min read`
      }));

      setPosts(formattedPosts);
      setIsLoading(false);
    };

    fetchPosts();
  }, []);
  return (
    <Layout>
      {/* Hero */}
      <section className="relative overflow-hidden bg-background pt-8 pb-12">
        <div className="absolute inset-0 z-0">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-accent/5 blur-[120px]" />
        </div>

        <div className="container-max text-center relative z-10">
          <span className="inline-block px-4 py-2 bg-accent/5 border border-accent/20 rounded-full text-accent-foreground text-[10px] font-black uppercase tracking-[0.2em] mb-8">
            The Journal
          </span>
          <h1 className="text-2xl md:text-4xl font-black text-primary mb-6 tracking-tighter uppercase">
            Institutional <span className="text-gradient-gold">Intelligence</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto font-medium leading-relaxed">
            Market decoding, strategic shifts, and psychological frameworks from the core research desk.
          </p>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="section-padding bg-background">
        <div className="container-max">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <Loader2 className="w-10 h-10 animate-spin text-accent" />
              <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Accessing Archives...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground font-medium">No research publications found.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
              {posts.map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="group"
                >
                  <article className="card-premium h-full flex flex-col p-6 hover:-translate-y-4 hover:bg-white bg-white/40 border-white/60">
                    <div className="aspect-[16/10] overflow-hidden rounded-2xl mb-8 relative">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 bg-white/90 backdrop-blur-md rounded-full text-[10px] font-black uppercase tracking-widest text-primary shadow-sm">
                          {post.category}
                        </span>
                      </div>
                    </div>

                    <div className="flex-1 flex flex-col px-2">
                      <div className="flex items-center gap-4 mb-4">
                        <span className="text-[10px] font-bold text-accent uppercase tracking-widest">{post.readTime}</span>
                        <div className="w-1 h-1 rounded-full bg-gray-300" />
                        <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                          {new Date(post.date).toLocaleDateString('en-IN', {
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                      </div>

                      <h2 className="text-xl font-black text-primary mb-4 uppercase tracking-tighter leading-tight group-hover:text-accent transition-colors">
                        {post.title}
                      </h2>

                      <p className="text-sm font-medium text-muted-foreground line-clamp-3 mb-8 leading-relaxed">
                        {post.excerpt}
                      </p>

                      <div className="mt-auto flex items-center justify-between pt-6 border-t border-gray-100">
                        <span className="text-xs font-black uppercase tracking-widest text-primary/40">Read Report</span>
                        <div className="w-10 h-10 rounded-full bg-primary/5 flex items-center justify-center group-hover:bg-accent group-hover:text-accent-foreground transition-all duration-500">
                          <ArrowRight className="w-4 h-4" />
                        </div>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default Blog;
